/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useState } from "react";

export default function RecentUploads({
  isOwnProfile,
  profileUID,
  recentUploads,
  onOpenRecentUpload,
  displayLocation,
  SectionRefreshOverlay,
  sectionLoading,
  sectionProgress,
}) {
  const [visibleUploadCount, setVisibleUploadCount] = useState(5);

  useEffect(() => {
    setVisibleUploadCount(5);
  }, [profileUID, recentUploads]);

  function loadMoreVisibleUploads(event) {
    const uploadList = event.currentTarget;
    const distanceFromBottom =
      uploadList.scrollHeight - uploadList.scrollTop - uploadList.clientHeight;

    if (distanceFromBottom <= 80 && visibleUploadCount < recentUploads.length) {
      setVisibleUploadCount((currentCount) =>
        Math.min(currentCount + 5, recentUploads.length),
      );
    }
  }

  return (
    <div className="lg:col-span-6 relative bg-white rounded-3xl shadow-lg border border-[#93ca87]/40 p-6">
      <SectionRefreshOverlay
        show={sectionLoading.taxonomy}
        progress={sectionProgress.taxonomy}
        label="Refreshing uploads"
      />

      <h2 className="text-xl font-semibold mb-6 text-center">Recent Uploads</h2>

      {recentUploads.length === 0 ? (
        <div className="text-sm text-slate-400 italic text-center py-10">
          {isOwnProfile
            ? "You haven’t uploaded any photos yet. Add your first observations to start building your collection."
            : "This user hasn’t uploaded any photos yet."}
        </div>
      ) : (
        <div
          onScroll={loadMoreVisibleUploads}
          className="space-y-3 max-h-[310px] overflow-y-auto pr-1"
        >
          {recentUploads.slice(0, visibleUploadCount).map((item, i) => (
            <div
              key={`${profileUID}-${item.speciesId}-${item.url || i}`}
              onClick={() => onOpenRecentUpload?.(i)}
              className="flex items-center gap-3 rounded-xl border border-[#93ca87]/40 bg-white p-1.5 cursor-pointer hover:shadow-md hover:-translate-y-[2px] transition"
            >
              <div className="w-16 h-14 shrink-0 rounded-xl overflow-hidden bg-slate-100">
                <img
                  src={item.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="min-w-0 text-left">
                <div className="text-xs text-[#374049] leading-snug">
                  {item.common ? (
                    <>
                      {item.common}{" "}
                      <span className="italic text-slate-600">
                        ({item.scientific})
                      </span>
                    </>
                  ) : (
                    <span className="italic">{item.scientific}</span>
                  )}
                </div>

                <div className="mt-1 space-y-0.5 text-[10px] leading-tight text-slate-500">
                  {item.created > 0 && (
                    <div>
                      Uploaded{" "}
                      {new Date(item.created * 1000).toLocaleDateString()}
                    </div>
                  )}

                  {displayLocation(item) &&
                    displayLocation(item) !== "Unknown" && (
                      <div>{displayLocation(item)}</div>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
